import FS from "@isomorphic-git/lightning-fs"
import FolderIcon from "@mui/icons-material/Folder"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import git, {ReadCommitResult, TREE, WalkerEntry} from "isomorphic-git"
import {useCallback, useContext, useEffect, useState} from "react"
import {Link, useLocation, useParams} from "react-router-dom"
import {BackendContext} from "../../../../context/BackendContext"
import {getTimeFromNow} from "../../../../functions/time"
import {useGitDir} from "../../../../hooks/useGitDir"
import {GitTree, TreeNodeMeta} from "../../../../interfaces/repos"
import styles from "./index.module.scss"

interface TreeProps {
  gitTree: GitTree
  cloneCnt: number
}

export const Tree = (props: TreeProps) => {
  const {orbitId, repoId, branch} = useParams()
  const gitDir = useGitDir(orbitId, repoId)
  const params = useParams()

  const {fs} = useContext(BackendContext)
  const location = useLocation()
  const [nodes, setNodes] = useState<TreeNodeMeta[]>([])
  const [lastCommit, setLastCommit] = useState<ReadCommitResult>()
  const [currentBranch, setCurrentBranch] = useState<string>()

  const getSubTree = useCallback(async (
    path: string,
    branch: string,
    fs: FS,
    gitTree: GitTree,
    gitDir: string,
    walkerEntry: WalkerEntry,
  ) => {
    const oid = await walkerEntry.oid()

    // Get latest commit
    let commits: ReadCommitResult[] = []
    try {
      commits = await git.log({
        fs,
        gitdir: gitDir,
        ref: branch,
        force: true,
        depth: 1
      })
    } catch (e) {
      setLastCommit(undefined)
      return
    }

    if (commits.length === 0) {
      setLastCommit(undefined)
      return
    }
    setLastCommit(commits[0])

    // Get entire subtree
    const walk = await git.walk({
      fs: fs,
      gitdir: gitDir,
      trees: [TREE({ref: oid})],
      map: async (filepath, [head, _]) => {
        const depth = filepath.split("/").length
        if (depth !== 1 || filepath === "." || !head) {
          return undefined
        }
        const lastCommit = await git.log({
          fs,
          gitdir: gitDir,
          ref: branch,
          filepath: path === "." ? filepath : `${path}/${filepath}`,
          force: true,
          depth: 1
        })
        if (lastCommit.length === 0) {
          return undefined
        }

        const isBlob = (await head.type()) === "blob"
        const oid = await head.oid()
        return {
          filepath,
          isBlob,
          oid,
          lastCommit: lastCommit[0].commit
        }
      }
    })

    const treeNodes: TreeNodeMeta[] = []
    for (const w of walk) {
      treeNodes.push({
        name: w.filepath,
        oid: w.oid,
        isBlob: w.isBlob,
        lastCommitMessage: w.lastCommit.message,
        lastUpdateTime: w.lastCommit.author.timestamp
      })
    }

    setNodes(treeNodes)
  }, [])

  useEffect(() => {
    const branch = params["branch"]
    if (branch == null || branch === "") {
      return
    }
    setCurrentBranch(branch)
  }, [params])

  useEffect(() => {
    const path = params["*"] || "."
    const gitTree = props.gitTree
    const walkerEntry = gitTree[path]

    if (!fs || !gitTree || !gitDir || !walkerEntry || !branch) {
      return
    }

    getSubTree(path, branch, fs, gitTree, gitDir, walkerEntry).then()
  }, [getSubTree, fs, params, props, gitDir, branch])


  return (
    <div className={styles.Container}>
      <div className={styles.HeaderRow}>
        {
          lastCommit && (
            <div className={styles.LastCommitMessage}>
              {lastCommit.commit.author.name} - {lastCommit.commit.message}
            </div>
          )
        }
        {
          lastCommit && (
            <div className={styles.LastCommitTime}>
              {getTimeFromNow(lastCommit.commit.author.timestamp)}
            </div>
          )
        }
      </div>
      <div className={styles.ItemList}>
        {
          nodes.map((node: TreeNodeMeta) => {
            const nodeType = node.isBlob ? "blob" : "tree"

            let newPath = location.pathname.replace(
              `codes/tree/${currentBranch}`,
              `codes/${nodeType}/${currentBranch}`
            )
            newPath = `${newPath}/${node.name}`

            return (
              <Link key={node.name}
                    to={newPath}
                    className={styles.Item}
              >
                {
                  node.isBlob ?
                    <InsertDriveFileIcon className={styles.Icon}/> :
                    <FolderIcon className={styles.Icon}/>
                }
                <div className={styles.ItemName}>{node.name}</div>
                <div className={styles.LastCommitMessage}>{node.lastCommitMessage}</div>
                <div className={styles.LastUpdate}>{getTimeFromNow(node.lastUpdateTime)}</div>
              </Link>
            )
          })
        }
      </div>
    </div>
  )
}
